import React, { useState, useEffect } from "react";
import styles from "./CertificatesScreen.module.scss";
import classNames from "classnames";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import Input from "../../../components/input/Input";
import Button from "../../../components/button/Button";
import certificateService from "../../../apis/services/admin/certificateService";
import ResourceList from "../../../components/resourceList/ResourceList";
import AreYouSureModal from "../../../components/modals/areYouSureModal/AreYouSureModal";
import { DeleteIcon } from "../../../assets";
import moment from "moment";
import { isMobile } from "react-device-detect";
import {
  fetchCertificates,
  deleteCertificate,
  deleteCertificateResetState,
} from "../../../store/actions/admin/certificates";

const HEADER_VALUES = [
  { label: "CERTIFICATE ID", key: "certificateId", sortable: true },
  { label: "KOMPANIJA", key: "companyName", sortable: true },
  { label: "POSLOVNI PROSTOR", key: "businessUnitName", sortable: true },
  { label: "VAŽI OD", key: "validFrom", sortable: true },
  { label: "VAŽI DO", key: "validTo", sortable: true },
  { label: "BROJ UREĐAJA", key: "fiscalDevicesCount", sortable: false },
  { label: "AKCIJE", sortable: false },
];

const CertificatesScreen = () => {
  const dispatch = useDispatch();
  const [certificate, setCertificate] = useState(null);
  const [pac, setPac] = useState("");
  const [password, setPassword] = useState("");
  const [packageFiles, setPackageFiles] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [packageUploading, setPackageUploading] = useState(false);
  const [packageResult, setPackageResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [certificateToDelete, setCertificateToDelete] = useState(null);

  const certificates = useSelector((state) => state.certificates.certificates);
  const deleteCertificateInfo = useSelector((state) => state.certificates.deleteCertificate);

  useEffect(() => {
    dispatch(fetchCertificates(1, 10, "", "id", "DESC"));
  }, [dispatch]);

  useEffect(() => {
    if (deleteCertificateInfo.success) {
      toast.success("Sertifikat uspešno obrisan");
      setDeleteModalOpen(false);
      setCertificateToDelete(null);
      dispatch(deleteCertificateResetState());
      dispatch(fetchCertificates(certificates.page, certificates.perPage, searchTerm, "id", "DESC"));
    }
  }, [deleteCertificateInfo.success]);

  useEffect(() => {
    if (deleteCertificateInfo.error) {
      toast.error("Greška prilikom brisanja sertifikata");
      dispatch(deleteCertificateResetState());
    }
  }, [deleteCertificateInfo.error]);

  const onPackageFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;
    setPackageResult(null);
    setPackageFiles((prev) => {
      const merged = [...prev];
      incoming.forEach((file) => {
        if (!merged.some((f) => f.name === file.name && f.size === file.size)) {
          merged.push(file);
        }
      });
      return merged;
    });
  };

  const handlePackageUpload = async () => {
    if (packageFiles.length === 0) {
      toast.error("Prevuci ZIP i PDF fajlove");
      return;
    }

    setPackageUploading(true);
    try {
      const response = await certificateService.uploadCertificatePackage(
        packageFiles
      );
      setPackageResult(response?.data || null);
      toast.success("Sertifikat instaliran");
      setPackageFiles([]);
      dispatch(
        fetchCertificates(
          certificates.page,
          certificates.perPage,
          searchTerm,
          "id",
          "DESC"
        )
      );
    } catch (error) {
      console.error("Error uploading certificate package:", error);
      toast.error(
        error.response?.data?.message ||
          "Došlo je do greške prilikom instalacije sertifikata"
      );
    } finally {
      setPackageUploading(false);
    }
  };

  const handleFileChange = (e) => {
    setCertificate(e.target.files[0]);
    setUploadSuccess(false);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (!certificate) {
      toast.error("Molimo izaberite sertifikat");
      return;
    }
    
    if (!pac) {
      toast.error("PAC je obavezan");
      return;
    }
    
    if (!password) {
      toast.error("Lozinka je obavezna");
      return;
    }
    
    setIsUploading(true);
    setUploadSuccess(false);
    
    try {
      await certificateService.uploadCertificate(certificate, pac, password);
      
      toast.success("Sertifikat uspešno otpremljen");
      setCertificate(null);
      setPac("");
      setPassword("");
      setUploadSuccess(true);
      
      // Reset the file input
      document.getElementById("certificate-input").value = "";
      
      // Refresh certificates list
      dispatch(fetchCertificates(certificates.page, certificates.perPage, searchTerm, "id", "DESC"));
    } catch (error) {
      console.error("Error uploading certificate:", error);
      toast.error(
        error.response?.data?.message || 
        "Došlo je do greške prilikom otpremanja sertifikata"
      );
    } finally {
      setIsUploading(false);
    }
  };

  const onLoadPage = (page) => {
    dispatch(fetchCertificates(page, certificates.perPage, searchTerm, "id", "DESC"));
  };

  const onSort = (sortBy, order) => {
    dispatch(fetchCertificates(certificates.page, certificates.perPage, searchTerm, sortBy, order));
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    dispatch(fetchCertificates(1, certificates.perPage, value, "id", "DESC"));
  };

  const getCertificateStatus = (validTo) => {
    if (!validTo) return null;
    
    const today = moment();
    const expiryDate = moment(validTo);
    const daysUntilExpiry = expiryDate.diff(today, 'days');
    
    if (daysUntilExpiry < 0) {
      return { type: 'expired', message: 'Istekao' };
    } else if (daysUntilExpiry <= 30) {
      return { type: 'warning', message: `Ističe za ${daysUntilExpiry} dana` };
    }
    
    return null;
  };

  const handleDeleteClick = (cert) => {
    setCertificateToDelete(cert);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (certificateToDelete) {
      dispatch(deleteCertificate(certificateToDelete.id));
    }
  };

  const renderCertificateRow = (cert, index) => {
    const status = getCertificateStatus(cert.validTo);
    const validFrom = cert.validFrom ? moment(cert.validFrom).format("DD-MM-YYYY") : "-";
    const validTo = cert.validTo ? moment(cert.validTo).format("DD-MM-YYYY") : "-";

    if (isMobile) {
      return (
        <div className={styles.listCard} key={cert.id}>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Certificate ID</p>
            <p className={styles.rowCardValue}>{cert.certificateId}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Kompanija</p>
            <p className={styles.rowCardValue}>{cert.businessUnit?.company?.name || "-"}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Poslovni prostor</p>
            <p className={styles.rowCardValue}>{cert.businessUnit?.name || "-"}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Važi od</p>
            <p className={styles.rowCardValue}>{validFrom}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Važi do</p>
            <div className={styles.flexColumn}>
              <p className={styles.rowCardValue}>{validTo}</p>
              {status && (
                <p className={classNames(
                  styles.statusBadge,
                  status.type === 'expired' ? styles.expired : styles.warning
                )}>
                  {status.message}
                </p>
              )}
            </div>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Broj uređaja</p>
            <p className={styles.rowCardValue}>{cert.fiscalDevicesCount || 0}</p>
          </div>
          <div className={styles.rowCard}>
            <img
              src={DeleteIcon}
              alt="delete"
              className={styles.deleteIcon}
              onClick={() => handleDeleteClick(cert)}
            />
          </div>
        </div>
      );
    }

    return (
      <div
        className={classNames(
          styles.row,
          index % 2 === 0 ? styles.even : styles.odd
        )}
        key={cert.id}
      >
        <div className={styles.cell}>
          <p>{cert.certificateId}</p>
        </div>
        <div className={styles.cell}>
          <p>{cert.businessUnit?.company?.name || "-"}</p>
        </div>
        <div className={styles.cell}>
          <p>{cert.businessUnit?.name || "-"}</p>
        </div>
        <div className={styles.cell}>
          <p>{validFrom}</p>
        </div>
        <div className={styles.cell}>
          <div className={styles.flexColumn}>
            <p>{validTo}</p>
            {status && (
              <p className={classNames(
                styles.statusBadge,
                status.type === 'expired' ? styles.expired : styles.warning
              )}>
                {status.message}
              </p>
            )}
          </div>
        </div>
        <div className={styles.cell}>
          <p>{cert.fiscalDevicesCount || 0}</p>
        </div>
        <div className={styles.cell}>
          <img
            src={DeleteIcon}
            alt="delete"
            className={styles.deleteIcon}
            onClick={() => handleDeleteClick(cert)}
          />
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <AreYouSureModal
        modalOpen={deleteModalOpen}
        closeModal={() => {
          setDeleteModalOpen(false);
          setCertificateToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Brisanje sertifikata"
        message={
          certificateToDelete && (
            <div>
              <p>Da li ste sigurni da želite da obrišete sertifikat:</p>
              <p><strong>{certificateToDelete.certificateId}</strong></p>
              {certificateToDelete.fiscalDevicesCount > 0 && (
                <p className={styles.warningText}>
                  <strong>Upozorenje:</strong> Ovaj sertifikat se koristi u poslovnom prostoru sa {certificateToDelete.fiscalDevicesCount} fiskalnih uređaja.
                </p>
              )}
            </div>
          )
        }
      />
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Upravljanje sertifikatima</h1>
        </div>
        
        <div className={styles.content}>
          <div className={classNames(styles.section)}>
            <div className={styles.sectionHeader}>
              <p className={styles.sectionTitle}>Otpremanje sertifikata</p>
            </div>
            <div className={classNames(styles.card)}>
              <div className={styles.cardContent}>
                {uploadSuccess && (
                  <div className={styles.successMessage}>
                    Sertifikat je uspešno otpremljen!
                  </div>
                )}
                
                <div
                  className={classNames(
                    styles.dropZone,
                    isDragActive && styles.dropZoneActive
                  )}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragActive(true);
                  }}
                  onDragLeave={() => setIsDragActive(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragActive(false);
                    onPackageFiles(e.dataTransfer.files);
                  }}
                  onClick={() =>
                    document.getElementById("certificate-package-input").click()
                  }
                >
                  <input
                    id="certificate-package-input"
                    type="file"
                    multiple
                    accept=".zip,.pdf,.p12,.pfx"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      onPackageFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                  <p className={styles.dropZoneTitle}>
                    Prevuci ZIP + PDF ovde ili klikni za izbor
                  </p>
                  <p className={styles.dropZoneHint}>
                    ZIP sa sertifikatom i PURS PDF — lozinka i PAK se čitaju
                    automatski
                  </p>
                </div>
                {packageFiles.length > 0 && (
                  <div>
                    {packageFiles.map((file) => (
                      <p key={file.name} className={styles.packageFileRow}>
                        {file.name}
                        <span
                          className={styles.removeFile}
                          onClick={() =>
                            setPackageFiles(
                              packageFiles.filter((f) => f !== file)
                            )
                          }
                        >
                          ✕
                        </span>
                      </p>
                    ))}
                    <div className={styles.buttonWrapper}>
                      <Button
                        name={
                          packageUploading
                            ? "Instaliranje..."
                            : "Instaliraj sertifikat"
                        }
                        onClick={handlePackageUpload}
                        disabled={packageUploading}
                      />
                    </div>
                  </div>
                )}
                {packageResult && (
                  <div className={styles.successMessage}>
                    Instaliran sertifikat {packageResult.certificateId} —{" "}
                    {packageResult.companyName} (PIB {packageResult.tin}),
                    poslovni prostor "{packageResult.businessUnitName}"
                    {packageResult.businessUnitAdopted
                      ? " (vezan za postojeću poslovnu jedinicu)"
                      : packageResult.businessUnitCreated
                      ? " (kreirana nova poslovna jedinica)"
                      : ""}
                    {packageResult.evictedCertificateId
                      ? `; prethodni sertifikat ${packageResult.evictedCertificateId} je zamenjen`
                      : ""}
                    {packageResult.validTo
                      ? `; važi do ${moment(packageResult.validTo).format(
                          "DD-MM-YYYY"
                        )}`
                      : ""}
                  </div>
                )}
                <p className={styles.orDivider}>ili ručno:</p>

                <div className={styles.form}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Sertifikat (.p12 fajl)</label>
                    <div className={styles.fileInputWrapper}>
                      <input
                        id="certificate-input"
                        type="file"
                        accept=".p12"
                        onChange={handleFileChange}
                        className={styles.fileInput}
                        disabled={isUploading}
                      />
                    </div>
                    {certificate && (
                      <p className={styles.fileName}>
                        Izabrani fajl: {certificate.name}
                      </p>
                    )}
                  </div>
                  
                  <div className={styles.inputWrapper}>
                    <p>PAC</p>
                    <Input 
                      onChange={setPac} 
                      value={pac} 
                      disabled={isUploading}
                    />
                  </div>
                  
                  <div className={styles.inputWrapper}>
                    <p>Lozinka</p>
                    <Input 
                      onChange={setPassword} 
                      value={password} 
                      type="password"
                      disabled={isUploading}
                    />
                  </div>
                  
                  <div className={styles.buttonWrapper}>
                    <Button 
                      name={isUploading ? "Otpremanje..." : "Otpremi sertifikat"} 
                      onClick={handleSubmit} 
                      disabled={isUploading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={classNames(styles.section, styles.marginTop)}>
            <div className={styles.sectionHeader}>
              <p className={styles.sectionTitle}>Lista sertifikata</p>
              <div className={styles.searchWrapper}>
                <Input
                  placeholder="Pretraži sertifikate..."
                  onChange={handleSearch}
                  value={searchTerm}
                />
              </div>
            </div>
            <ResourceList
              resource={certificates}
              onSort={onSort}
              onLoadPage={onLoadPage}
              headerValues={HEADER_VALUES}
              rowRender={renderCertificateRow}
              showLoader={true}
            />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CertificatesScreen; 